/*
  Tearstones loose stone detail navigation

  Resolves legacy collection detail links to native server-rendered product URLs.
  Shopify supplies trusted store hosts and localized collection routes. Product
  visits, commerce requests, quick-add loading and cart actions remain native.
*/

const configElement = document.querySelector('[data-tearstones-product-routing]');
let config;
try {
  config = JSON.parse(configElement?.textContent || 'null');
} catch {
  config = null;
}

const currentUrl = new URL(location.href);
const specialRequest = (url) => {
  if (['section_id', 'sections', 'sections_url'].some((key) => url.searchParams.has(key))) return true;
  const view = url.searchParams.get('view');
  return Boolean(view && view !== 'stone-detail');
};

function storeHosts(values) {
  const hosts = new Set([location.host.toLowerCase()]);
  for (const value of values || []) {
    if (!value) continue;
    try {
      const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
      if (/^https?:$/.test(url.protocol)) hosts.add(url.host.toLowerCase());
    } catch { /* Ignore an unavailable optional Shopify host field. */ }
  }
  return hosts;
}

function isStoreUrl(url, hosts) {
  return /^https?:$/.test(url.protocol) && !url.username && !url.password && hosts.has(url.host.toLowerCase());
}

function productDetailUrl(source, hosts) {
  if (source.searchParams.get('view') !== 'stone-detail') return null;
  const handle = source.searchParams.get('product');
  if (!handle || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) return null;

  let collectionPath;
  for (const route of Object.values(config.collections || {})) {
    try {
      const configured = new URL(route, location.origin);
      const path = configured.pathname.replace(/\/$/, '');
      if (isStoreUrl(configured, hosts) &&
        (source.pathname === path || source.pathname.startsWith(path + '/'))) {
        collectionPath = path;
        break;
      }
    } catch { /* Ignore unavailable optional collection routes. */ }
  }
  if (!collectionPath) return null;
  const productPath = collectionPath.replace(/\/collections\/[^/]+$/, '/products/');
  const target = new URL(productPath + encodeURIComponent(handle), location.origin);
  for (const [key, value] of source.searchParams) {
    if (!['view', 'product', 'shape', 'family'].includes(key)) target.searchParams.append(key, value);
  }
  if (!target.searchParams.has('preview_theme_id') && currentUrl.searchParams.has('preview_theme_id')) {
    target.searchParams.set('preview_theme_id', currentUrl.searchParams.get('preview_theme_id'));
  }
  target.hash = source.hash;
  return target;
}

function startRouting() {
  if (!config || config.designMode || window.Shopify?.designMode ||
    document.documentElement.dataset.designMode === 'true') return;
  if (['section_id', 'sections', 'sections_url'].some((key) => currentUrl.searchParams.has(key))) return;
  if (specialRequest(currentUrl)) return;

  const hosts = storeHosts(config.allowedHosts);
  if (config.pageType === 'collection' && isStoreUrl(currentUrl, hosts)) {
    const destination = productDetailUrl(currentUrl, hosts);
    if (destination && destination.href !== currentUrl.href) {
      location.replace(destination.href);
      return;
    }
  }

  const rewrite = (anchor) => {
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.hasAttribute('href') || anchor.hasAttribute('download')) {
      return;
    }
    if (anchor.closest('[data-quick-add-url], [data-stone-routing="native"]') ||
      anchor.getAttribute('role') === 'button') return;
    const raw = anchor.getAttribute('href');
    if (!raw || raw.startsWith('#')) return;
    let url;
    try {
      url = new URL(raw, location.href);
    } catch {
      return;
    }
    if (!isStoreUrl(url, hosts) || specialRequest(url)) return;
    const destination = productDetailUrl(url, hosts);
    if (!destination) return;
    const href = `${destination.pathname}${destination.search}${destination.hash}`;
    if (raw !== href) anchor.setAttribute('href', href);
  };

  const scan = (root) => {
    if (root instanceof HTMLAnchorElement) rewrite(root);
    root.querySelectorAll?.('a[href]').forEach(rewrite);
  };
  scan(document);

  const pending = new Set();
  let scheduled = false;
  const enqueue = (root) => {
    if (!(root instanceof Element)) return;
    pending.add(root);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const roots = [...pending];
      pending.clear();
      for (const entry of roots) {
        if (entry.isConnected) scan(entry);
      }
    });
  };
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') enqueue(record.target);
      else record.addedNodes.forEach(enqueue);
    }
  });
  observer.observe(document.documentElement, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['href'],
  });

  const beforeNavigation = (event) => {
    const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (target) rewrite(target);
  };
  document.addEventListener('click', beforeNavigation, { capture: true });
  document.addEventListener('auxclick', beforeNavigation, { capture: true });
}

startRouting();
